import { atom } from "jotai";

import { DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { type DocsListLocalEntry } from "../document-list";
import { env } from "../env";
import { fs } from "../filesystem";
import { paths } from "../paths";

const logger = new Logger("disk");

const $diskDocuments = atom(async (get): Promise<Record<string, DocsListLocalEntry>> => {
	const fsApi = fs();
	const pathsApi = paths();

	const documentsDirPath = await get(pathsApi.getDocumentsDirPath());
	let exists = await get(fsApi.exists({ filePath: documentsDirPath }));
	if (!exists) {
		return {};
	}

	const dirList = await get(fsApi.readDir({ dirPath: documentsDirPath }));
	const validDirs = dirList.filter(
		(dir) => dir.isDirectory && !dir.isSymlink && !dir.name.startsWith(".")
	);

	// Fetch metadata for each document
	const documentMetadatas: Record<string, DocsListLocalEntry> = {};

	await Promise.all(
		validDirs.map(async (dir: { name: string; isDirectory: boolean; isSymlink: boolean }) => {
			try {
				const documentDirPath = await get(pathsApi.getDocumentDirPath(dir.name));
				const metadataFilePath = await get(pathsApi.getDocumentMetadataFilePath(dir.name));

				// Read metadata file using readYamlFile atom, parse and validate.
				const parsed = await get(fsApi.readYamlFile({ filePath: metadataFilePath }));
				const result = DocumentMetadataSchema.safeParse(parsed);

				if (result.error) {
					logger.warn("Document has invalid metadata, skipping", {
						path: documentDirPath,
						metadata: parsed,
						cause: result.error,
					});
					return;
				}

				const data = result.data;

				// Ensure dates are valid
				const createdAt =
					data.created && !isNaN(data.created.getTime()) ? data.created : new Date();
				const updatedAt =
					data.updated && !isNaN(data.updated.getTime()) ? data.updated : createdAt;

				// Transform DocumentMetadata to DocsListLocalEntry
				documentMetadatas[data.id] = {
					title: data.title,
					createdAt,
					updatedAt,
					path: documentDirPath,
					type: data.type,
				};
			} catch (error) {
				logger.error("Unable to read or parse document metadata, skipping", {
					dir: dir.name,
					cause: error,
				});
			}
		})
	);

	return documentMetadatas;
});
$diskDocuments.debugLabel = "diskDocuments";

const tauriDiskAPI = {
	documents: {
		getAll: $diskDocuments,
	},
};
type DiskAPI = typeof tauriDiskAPI;

const noOpDiskAPI: DiskAPI = {
	documents: { getAll: atom(async () => ({})) },
};

export const disk = () => {
	if (env.isSsr || !env.isDesktopApp) {
		return noOpDiskAPI;
	}

	return tauriDiskAPI;
};
